import rawSeatMatrix from '../data/ugmac/ugmacSeatMatrix.generated.json';
import rawColleges from '../data/ugmac/ugmacColleges.generated.json';
import rawCutoff from '../data/ugmac/UGMAC_Medical_Cutoffs_2025.json';
import { matchesSelection } from './filterSelection.js';

const categoryOrder = [
  'UR',
  'F_UR',
  'SC',
  'F_SC',
  'ST',
  'F_ST',
  'EBC',
  'F_EBC',
  'BC',
  'F_BC',
  'RCG',
  'EWS',
  'F_EWS',
  'DQ',
  'Muslim Minority',
  'Sikh Minority',
  'NRI',
  'Ward of BASU',
];

export const ugmacSeatMatrixRows = rawSeatMatrix;
export const ugmacColleges = rawColleges;
export const ugmacCutoffRows = rawCutoff;
export const ugmacSeatTypeOptions = ['General', 'Female'];
export const ugmacCutoffRoundOptions = [{ key: 'round3', label: 'Round 3' }];

const ugmacCollegePreferenceMatchers = [
  { key: 'PMCH', matcher: (name) => name.includes('P.M.C.PATNA') },
  { key: 'IGIMS', matcher: (name) => name.includes('I.G.I.M.S. PATNA') },
  { key: 'NMCH', matcher: (name) => name.includes('N.M.C.PATNA') },
  { key: 'DMCH', matcher: (name) => name.includes('D.M.C.LAHERIASARAI') },
  { key: 'SKMCH', matcher: (name) => name.includes('S.K.M.C.MUZAFFARPUR') },
  { key: 'VIMS', matcher: (name) => name.includes('B.M.I.M.S.PAWAPURI') },
  { key: 'JLNMC', matcher: (name) => name.includes('J.L.N.M.C.BHAGALPUR') },
  { key: 'ANMMCH', matcher: (name) => name.includes('A.N.M.M.C.GAYA') },
  { key: 'Bettiah', matcher: (name) => name.includes('G. M. C. BETTIAH') },
  { key: 'Purnea', matcher: (name) => name.includes('G.M.C. PURNEA') },
  { key: 'Madhepura', matcher: (name) => name.includes('J.K.T.M.C.& H. MADHEPURA') },
  { key: 'Chhapra', matcher: (name) => name.includes('G.M.C. CHHAPRA') || name.includes('CHHAPRA') },
  { key: 'Samastipur', matcher: (name) => name.includes('SAMASTIPUR') },
  { key: 'Sitamarhi', matcher: (name) => name.includes('SITAMARHI') },
];

const ugmacCoursePreferenceOrder = ['M.B.B.S.', 'B.D.S.', 'B.V.Sc.&A.H.'];

const femaleCategoryMap = {
  UR: 'F_UR',
  SC: 'F_SC',
  ST: 'F_ST',
  EBC: 'F_EBC',
  BC: 'F_BC',
  EWS: 'F_EWS',
};

const generalCategoryOrder = [
  'UR',
  'SC',
  'ST',
  'EBC',
  'BC',
  'RCG',
  'EWS',
  'DQ',
  'Muslim Minority',
  'Sikh Minority',
  'NRI',
  'Ward of BASU',
];

const femaleCategoryOrder = ['UR', 'SC', 'ST', 'EBC', 'BC', 'EWS'];

const ugmacCourseStreamMap = new Map();
rawSeatMatrix.forEach((row) => {
  if (row.course && row.stream && !ugmacCourseStreamMap.has(row.course)) {
    ugmacCourseStreamMap.set(row.course, row.stream);
  }
});
rawCutoff.forEach((row) => {
  if (row.course && row.stream && !ugmacCourseStreamMap.has(row.course)) {
    ugmacCourseStreamMap.set(row.course, row.stream);
  }
});

const deriveCityFromInstitution = (name = '') => {
  const cleaned = String(name).replace(/\./g, ' ').replace(/\s+/g, ' ').trim();
  if (!cleaned) return 'Bihar';
  if (cleaned.includes(',')) {
    const last = cleaned.split(',').pop()?.trim();
    return last || 'Bihar';
  }
  const tokens = cleaned.split(' ');
  return tokens[tokens.length - 1] || 'Bihar';
};

export const buildUgmacMapsLink = (name) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, Bihar`)}`;

export const ugmacCategoryLabels = {
  All: 'All categories',
  UR: 'UR',
  F_UR: 'F_UR',
  SC: 'SC',
  F_SC: 'F_SC',
  ST: 'ST',
  F_ST: 'F_ST',
  EBC: 'EBC',
  F_EBC: 'F_EBC',
  BC: 'BC',
  F_BC: 'F_BC',
  RCG: 'RCG',
  EWS: 'EWS',
  F_EWS: 'F_EWS',
  DQ: 'DQ',
  'Muslim Minority': 'Muslim Minority',
  'Sikh Minority': 'Sikh Minority',
  NRI: 'NRI',
  'Ward of BASU': 'Ward of BASU',
  'Ward Quota': 'Ward Quota',
};

export const getUgmacCollegePreferenceRank = (institutionName = '') => {
  const matchIndex = ugmacCollegePreferenceMatchers.findIndex(({ matcher }) =>
    matcher(String(institutionName)),
  );
  return matchIndex === -1 ? Number.MAX_SAFE_INTEGER : matchIndex;
};

export const compareUgmacCollegePreference = (firstName = '', secondName = '') =>
  getUgmacCollegePreferenceRank(firstName) - getUgmacCollegePreferenceRank(secondName) ||
  String(firstName).localeCompare(String(secondName));

export const getUgmacCoursePreferenceRank = (courseName = '') => {
  const matchIndex = ugmacCoursePreferenceOrder.findIndex(
    (preferredCourse) => preferredCourse === String(courseName),
  );
  return matchIndex === -1 ? Number.MAX_SAFE_INTEGER : matchIndex;
};

export const compareUgmacCoursePreference = (firstCourse = '', secondCourse = '') =>
  getUgmacCoursePreferenceRank(firstCourse) - getUgmacCoursePreferenceRank(secondCourse) ||
  String(firstCourse).localeCompare(String(secondCourse));

export const getUgmacCourseDisplayName = (courseName = '', streamName = '') => {
  const resolvedStream = streamName || ugmacCourseStreamMap.get(courseName) || '';
  return resolvedStream ? `${courseName} (${resolvedStream})` : courseName;
};

export const ugmacInstitutionOptions = [...new Set(ugmacSeatMatrixRows.map((row) => row.institution))].sort();
export const ugmacCourseOptions = [...new Set(ugmacSeatMatrixRows.map((row) => row.course))].sort();
export const ugmacInstituteTypeOptions = [...new Set(ugmacSeatMatrixRows.map((row) => row.instituteType))].sort();
export const ugmacStreamOptions = [...new Set(ugmacSeatMatrixRows.map((row) => row.stream))].sort();
export const ugmacSeatSchemeOptions = [...new Set(ugmacSeatMatrixRows.map((row) => row.seatScheme))].sort();
export const ugmacCutoffInstitutionOptions = [...new Set(ugmacCutoffRows.map((row) => row.college))].sort();
export const ugmacCutoffCourseOptions = [...new Set(ugmacCutoffRows.map((row) => row.course))].sort(compareUgmacCoursePreference);
export const ugmacCutoffInstituteTypeOptions = [...new Set(ugmacCutoffRows.map((row) => row.instituteType))].sort();

export const getUgmacCategoryOptions = (rows) => {
  const available = new Set();
  rows.forEach((row) => {
    Object.keys(row.counts || {}).forEach((key) => {
      if ((row.counts?.[key] ?? 0) > 0) {
        available.add(key);
      }
    });
  });

  return [
    'All',
    ...categoryOrder.filter((category) => available.has(category)),
    ...[...available].filter((category) => !categoryOrder.includes(category)).sort(),
  ];
};

export const getUgmacCategoryOptionsBySeatType = (rows, seatType = 'General') => {
  const available = new Set();

  rows.forEach((row) => {
    if (seatType === 'Female') {
      Object.entries(femaleCategoryMap).forEach(([baseCategory, femaleKey]) => {
        if ((row.counts?.[femaleKey] ?? 0) > 0) {
          available.add(baseCategory);
        }
      });
      return;
    }

    generalCategoryOrder.forEach((category) => {
      if ((row.counts?.[category] ?? 0) > 0) {
        available.add(category);
      }
    });
  });

  const order = seatType === 'Female' ? femaleCategoryOrder : generalCategoryOrder;
  return ['All', ...order.filter((category) => available.has(category))];
};

export const getUgmacCutoffCategoryOptionsBySeatType = (rows, seatType = 'General') => {
  const available = new Set();

  rows.forEach((row) => {
    if (seatType === 'Female') {
      if (row.seatType === 'Female') {
        available.add(row.category);
      }
      return;
    }

    if (row.seatType === 'General') {
      available.add(row.category);
    }
  });

  const order = seatType === 'Female' ? femaleCategoryOrder : generalCategoryOrder;
  const additional = [...available]
    .filter((category) => !order.includes(category))
    .sort((first, second) => first.localeCompare(second));

  return ['All', ...order.filter((category) => available.has(category)), ...additional];
};

const getUgmacSeatCount = (row, seatType = 'General', category = 'All') => {
  const counts = row.counts || {};

  if (seatType === 'Female') {
    if (category === 'All') {
      return Object.values(femaleCategoryMap).reduce((sum, femaleKey) => sum + (counts[femaleKey] ?? 0), 0);
    }

    const femaleKey = femaleCategoryMap[category];
    return femaleKey ? counts[femaleKey] ?? 0 : 0;
  }

  if (category === 'All') {
    return generalCategoryOrder.reduce((sum, key) => sum + (counts[key] ?? 0), 0);
  }

  return counts[category] ?? 0;
};

export const filterUgmacSeatMatrixRows = ({
  institution = 'All',
  course = 'All',
  instituteType = 'All',
  stream = 'All',
  seatScheme = 'All',
  seatType = 'General',
  category = 'All',
} = {}) =>
  ugmacSeatMatrixRows
    .filter((row) => matchesSelection(row.institution, institution))
    .filter((row) => matchesSelection(row.course, course))
    .filter((row) => instituteType === 'All' || row.instituteType === instituteType)
    .filter((row) => stream === 'All' || row.stream === stream)
    .filter((row) => seatScheme === 'All' || row.seatScheme === seatScheme)
    .map((row) => ({
      ...row,
      selectedCategorySeats: getUgmacSeatCount(row, seatType, category),
    }))
    .filter((row) => (category === 'All' ? true : row.selectedCategorySeats > 0))
    .filter((row) => row.selectedCategorySeats > 0)
    .sort(
      (first, second) =>
        first.instituteType.localeCompare(second.instituteType) ||
        first.stream.localeCompare(second.stream) ||
        first.institution.localeCompare(second.institution) ||
        first.course.localeCompare(second.course),
    );

export const filterUgmacCutoffRows = ({
  roundKey = 'round3',
  institution = 'All',
  course = 'All',
  instituteType = 'All',
  seatType = 'General',
  category = 'All',
} = {}) =>
  ugmacCutoffRows
    .filter((row) => roundKey === 'All' || row.roundKey === roundKey)
    .filter((row) => matchesSelection(row.college, institution))
    .filter((row) => matchesSelection(row.course, course))
    .filter((row) => instituteType === 'All' || row.instituteType === instituteType)
    .filter((row) => seatType === 'All' || row.seatType === seatType)
    .filter((row) => category === 'All' || row.category === category)
    .sort(
      (first, second) =>
        compareUgmacCollegePreference(first.college, second.college) ||
        compareUgmacCoursePreference(first.course, second.course) ||
        first.seatType.localeCompare(second.seatType) ||
        categorySortValue(first.category) - categorySortValue(second.category),
    );

export const filterUgmacColleges = ({
  institution = 'All',
  course = 'All',
  instituteType = 'All',
  stream = 'All',
} = {}) =>
  ugmacColleges
    .filter((college) => matchesSelection(college.name, institution))
    .filter((college) => instituteType === 'All' || college.instituteType === instituteType)
    .filter((college) => stream === 'All' || college.streams.includes(stream))
    .filter((college) => matchesSelectionAny(college.courses, course))
    .sort(
      (first, second) =>
        first.instituteType.localeCompare(second.instituteType) ||
        first.name.localeCompare(second.name),
    );

function matchesSelectionAny(candidates = [], selected) {
  return candidates.some((candidate) => matchesSelection(candidate, selected)) || (Array.isArray(selected) ? selected.length === 0 : selected === 'All');
}

export const getUgmacCollegeBySlug = (slug) => {
  const college =
    ugmacColleges.find((item) => item.slug === slug) ??
    (() => {
      const cutoffRows = ugmacCutoffRows.filter((item) => item.slug === slug);
      if (!cutoffRows.length) {
        return null;
      }

      const firstRow = cutoffRows[0];
      return {
        name: firstRow.college,
        slug: firstRow.slug,
        instituteType: firstRow.instituteType,
        streams: [...new Set(cutoffRows.map((row) => row.stream))].sort(),
        courses: [...new Set(cutoffRows.map((row) => row.course))].sort(compareUgmacCoursePreference),
        seatSchemes: ['State Quota / Uploaded Rounds'],
        totalSeats: 0,
        rowCount: 0,
      };
    })();
  if (!college) {
    return null;
  }

  const seatRows = ugmacSeatMatrixRows
    .filter((row) => row.institution === college.name)
    .sort(
      (first, second) =>
        first.stream.localeCompare(second.stream) ||
        first.course.localeCompare(second.course) ||
        first.seatScheme.localeCompare(second.seatScheme),
    );

  return {
    ...college,
    city: deriveCityFromInstitution(college.name),
    googleMapsLink: buildUgmacMapsLink(college.name),
    seatRows,
    cutoffRows: filterUgmacCutoffRows({ institution: college.name }),
    categories: getUgmacCategoryOptions(seatRows).filter((item) => item !== 'All'),
  };
};

function categorySortValue(category = '') {
  const index = generalCategoryOrder.indexOf(category);
  if (index !== -1) {
    return index;
  }
  if (category === 'Muslim Minority') return 100;
  if (category === 'Sikh Minority') return 101;
  if (category === 'NRI') return 102;
  if (category === 'Ward Quota') return 103;
  return 999;
}
